import { t } from 'i18next';
import { LeafBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { MatchingFill } from '@peacecover/icon';
import ChatItem from './ChatItem';
import type { MatchedTradingPhotoCardType, BasePhotoCardType } from '../types/MarketCardType';

interface ExchangePhotoCardItemProps {
  photoCard: MatchedTradingPhotoCardType;
}

const ExchangePhotoCardItem = ({ photoCard }: ExchangePhotoCardItemProps) => {
  const myPhotoCard = photoCard.sourceTradingPhotoCard.isMine
    ? photoCard.sourceTradingPhotoCard.photoCard
    : photoCard.targetTradingPhotoCard.photoCard;

  const otherPhotoCard = photoCard.sourceTradingPhotoCard.isOther
    ? photoCard.sourceTradingPhotoCard.photoCard
    : photoCard.targetTradingPhotoCard.photoCard;

  const exchangeStatus = photoCard.status === 'pending' || photoCard.status === 'ongoing';

  const requestChatCount = photoCard.chatRooms.filter(
    (chatRoom) => chatRoom.reviewStatus === 'request'
  ).length;

  return (
    <div className="w-full rounded-[16px] bg-background-light dark:bg-background-dark pb-[4px] border border-separator1 dark:border-[#141414] shadow-[0_4px_10px_0_rgba(0,0,0,0.04),0_0_24px_0_rgba(255,255,255,0.30)_inset] backdrop-blur-[20px] dark:shadow-[0_0_3px_0_rgba(0,0,0,0.83),0_0_12px_0_#000]">
      <div className="flex flex-row items-center justify-between p-[16px] shadow-[inset_0_-1px_0_0_var(--color-separator1)] dark:shadow-[inset_0_-1px_0_0_var(--color-separator2)]">
        <div className="flex gap-[4px] items-center">
          <MyExchangePhotoCard myPhotoCard={myPhotoCard} />
          <div className="flex flex-col gap-[5px] items-center">
            <MatchingFill
              size={16}
              className={exchangeStatus ? 'text-on-success' : 'text-text-sub-lv1'}
            />
            <div
              className={`w-[34px] rounded-[20px] ${
                exchangeStatus ? 'bg-success text-on-success' : 'bg-surface-lv2 text-text-sub-lv1'
              } px-[5px] py-[2px] font-semibold text-[9px] leading-none`}
            >
              {exchangeStatus
                ? t('NewTradingPhotoCardMarket.exchangeStatus.ongoing')
                : t('NewTradingPhotoCardMarket.exchangeStatus.completed')}
            </div>
          </div>
          <OtherExchangePhotoCard otherPhotoCard={otherPhotoCard} />
        </div>
      </div>
      <div>
        {photoCard.chatRooms.map((chatRoom) => (
          <ChatItem
            key={chatRoom.chatRoomId}
            chat={chatRoom}
            tradingPhotoCardId={photoCard.matchedTradingPhotoCardId}
            requestChatCount={requestChatCount}
          />
        ))}
      </div>
    </div>
  );
};

export default ExchangePhotoCardItem;

const MyExchangePhotoCard = ({ myPhotoCard }: { myPhotoCard: BasePhotoCardType }) => {
  const eventCategory = myPhotoCard.eventCategory;
  const category = myPhotoCard.category;
  return (
    <div className="flex gap-[8px] items-center">
      <div className="relative">
        <ThumbnailPhotoCard
          imageUrl={myPhotoCard.mediumThumbnailUrl || myPhotoCard.pictureUrl}
          className="w-[29px] h-[46px] rounded-[4px]"
        />
        <LeafBadge
          size="small"
          state="inactive"
          position="left"
          text={t('NewTradingPhotoCardMarket.my') as string}
          className="absolute bottom-0 left-0 bg-pure-black"
        />
      </div>
      <div className="w-[90px] flex flex-col gap-[5px] leading-none">
        <p className="h-[14px] text-[12px] font-semibold text-on-label truncate">
          {t('NewTradingPhotoCardMarket.photoCardTitle', {
            artist: myPhotoCard.memberName,
            category: t(`PhotoCardCategory.${eventCategory}.${category}.text`)
          })}
        </p>
        <p className="h-[13px] text-[11px] text-text-sub-lv1 truncate">{myPhotoCard.eventName}</p>
      </div>
    </div>
  );
};

const OtherExchangePhotoCard = ({ otherPhotoCard }: { otherPhotoCard: BasePhotoCardType }) => {
    const eventCategory = otherPhotoCard.eventCategory;
    const category = otherPhotoCard.category;
    return (
      <div className="flex gap-[8px] items-center">
        <div className="w-[90px] items-end flex flex-col gap-[5px] leading-none">
          <p className="h-[14px] w-full text-[12px] font-semibold text-on-label text-right truncate">
            {t('NewTradingPhotoCardMarket.photoCardTitle', {
              artist: otherPhotoCard.memberName,
              category: t(`PhotoCardCategory.${eventCategory}.${category}.text`)
            })}
          </p>
          {/* 디자인 시안과 맞추기 위해 빈 공백 추가 */}
          <p className="h-[13px] text-[11px] text-text-sub-lv1 w-full text-right truncate">
            &nbsp;&nbsp;{otherPhotoCard.eventName}
          </p>
        </div>
        <div className="relative">
          <ThumbnailPhotoCard
            imageUrl={otherPhotoCard.mediumThumbnailUrl || otherPhotoCard.pictureUrl}
            className="w-[29px] h-[46px] rounded-[4px]"
          />
          <LeafBadge
            size="small"
            state="inactive"
            position="right"
            text={t('NewTradingPhotoCardMarket.other') as string}
            className="absolute bottom-0 right-0 bg-pure-black"
          />
        </div>
      </div>
    );
};
